import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Database, Globe, Share2, Loader2 } from 'lucide-react'
import type { DataSourceType, CreateDataSourceRequest } from '@/lib/api/types'
import { cn } from '@/lib/utils'

interface CreateDataSourceDialogProps {
  open: boolean
  onClose: () => void
  onCreate: (data: CreateDataSourceRequest) => Promise<void>
}

const typeOptions: { value: DataSourceType; label: string; icon: React.ElementType; description: string }[] = [
  {
    value: 'CRM',
    label: 'CRM',
    icon: Database,
    description: 'Connect to Salesforce, HubSpot, or other CRM systems',
  },
  {
    value: 'CustomApi',
    label: 'Custom API',
    icon: Globe,
    description: 'Connect to any REST API endpoint',
  },
  {
    value: 'SharePoint',
    label: 'SharePoint',
    icon: Share2,
    description: 'Connect to SharePoint lists and libraries',
  },
]

export function CreateDataSourceDialog({
  open,
  onClose,
  onCreate,
}: CreateDataSourceDialogProps) {
  const [step, setStep] = useState<'type' | 'details'>('type')
  const [selectedType, setSelectedType] = useState<DataSourceType | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    apiEndpoint: '',
    apiKey: '',
    cacheDurationMinutes: 60,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleTypeSelect = (type: DataSourceType) => {
    setSelectedType(type)
    setStep('details')
  }

  const handleBack = () => {
    setStep('type')
    setError(null)
  }

  const handleClose = () => {
    setStep('type')
    setSelectedType(null)
    setFormData({
      name: '',
      description: '',
      apiEndpoint: '',
      apiKey: '',
      cacheDurationMinutes: 60,
    })
    setError(null)
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedType) return

    setLoading(true)
    setError(null)

    try {
      await onCreate({
        type: selectedType,
        name: formData.name,
        description: formData.description || undefined,
        apiEndpoint: formData.apiEndpoint || undefined,
        apiKey: formData.apiKey || undefined,
        cacheDurationMinutes: formData.cacheDurationMinutes,
      })
      handleClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create data source')
    } finally {
      setLoading(false)
    }
  }

  const isFormValid = formData.name.trim().length > 0 && (
    selectedType !== 'CustomApi' || formData.apiEndpoint.trim().length > 0
  )

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {step === 'type' ? 'Add Data Source' : `New ${selectedType} Source`}
          </DialogTitle>
          <DialogDescription>
            {step === 'type'
              ? 'Select the type of data source you want to add.'
              : 'Configure your data source connection.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'type' ? (
          <div className="grid gap-3 py-4">
            {typeOptions.map((option) => {
              const Icon = option.icon
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTypeSelect(option.value)}
                  className={cn(
                    'flex items-start gap-4 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700',
                    'hover:border-neutral-300 dark:hover:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800/50',
                    'transition-colors text-left'
                  )}
                >
                  <div className="p-2 rounded-lg bg-neutral-100 dark:bg-neutral-800">
                    <Icon className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-neutral-900 dark:text-neutral-100">
                      {option.label}
                    </h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      {option.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-neutral-900 dark:text-neutral-100"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="name"
                  placeholder="e.g., Production CRM"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="description"
                  className="text-sm font-medium text-neutral-900 dark:text-neutral-100"
                >
                  Description
                </label>
                <Input
                  id="description"
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>

              {selectedType === 'CustomApi' && (
                <>
                  <div className="space-y-2">
                    <label
                      htmlFor="apiEndpoint"
                      className="text-sm font-medium text-neutral-900 dark:text-neutral-100"
                    >
                      API Endpoint <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="apiEndpoint"
                      type="url"
                      placeholder="https://api.example.com/data"
                      value={formData.apiEndpoint}
                      onChange={(e) => setFormData((prev) => ({ ...prev, apiEndpoint: e.target.value }))}
                      required
                    />
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      The URL of the API endpoint to fetch data from.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="apiKey"
                      className="text-sm font-medium text-neutral-900 dark:text-neutral-100"
                    >
                      API Key
                    </label>
                    <Input
                      id="apiKey"
                      type="password"
                      placeholder="Optional API key"
                      value={formData.apiKey}
                      onChange={(e) => setFormData((prev) => ({ ...prev, apiKey: e.target.value }))}
                    />
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      If required, enter the API key for authentication.
                    </p>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="cacheDuration"
                  className="text-sm font-medium text-neutral-900 dark:text-neutral-100"
                >
                  Cache Duration
                </label>
                <Select
                  value={formData.cacheDurationMinutes.toString()}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, cacheDurationMinutes: parseInt(value) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="360">6 hours</SelectItem>
                    <SelectItem value="1440">24 hours</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  How long to cache data before refreshing from the source.
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-md bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleBack} disabled={loading}>
                Back
              </Button>
              <Button type="submit" disabled={!isFormValid || loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Data Source
              </Button>
            </DialogFooter>
          </form>
        )}

        {step === 'type' && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
